import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  CreditCard,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Settings,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Id } from "@/convex/_generated/dataModel";
import { useState } from "react";
import { useToastMutation } from "@/hooks/useToastMutation";
import { SubscriptionStatusCard } from "@/components/subscriptions/subscription-status-card";
import { SubscriptionStatusCardSkeleton } from "@/components/subscriptions/subscription-status-card-skeleton";
import { SubscriptionModulesManager } from "@/components/subscriptions/subscription-modules-manager";
import {
  ChangePlanModal,
  ManualActivateModal,
  ExtendTrialModal,
} from "@/components/subscriptions/subscription-admin-modals";
import { SubscriptionLimitsModal } from "@/components/subscriptions/subscriptions-table/subscription-limits-modal";
import type { SubscriptionData } from "@/components/subscriptions/subscriptions-table/subscriptions-data-table";

export const Route = createFileRoute(
  "/_super/super/control-panel/$companyId/subscription",
)({
  component: SubscriptionManagement,
});

function SubscriptionManagement() {
  const { companyId } = Route.useParams();
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isEditLimitsOpen, setIsEditLimitsOpen] = useState(false);
  const [isManualActivateOpen, setIsManualActivateOpen] = useState(false);
  const [isExtendTrialOpen, setIsExtendTrialOpen] = useState(false);

  const details = useQuery(
    api.companies.subscriptions.queries.getCompanySubscriptionDetails,
    {
      companyId: companyId as Id<"companies">,
    },
  );

  const { execute: cancelSubscription, bar: cancelBar } = useToastMutation(
    api.companies.subscriptions.mutations.cancelSubscription,
    {
      loadingMessage: "Cancelando suscripción...",
      successMessage: "Suscripción cancelada exitosamente",
      errorMessage: "Error al cancelar la suscripción",
    },
  );

  const { execute: reactivateSubscription, bar: reactivateBar } =
    useToastMutation(
      api.companies.subscriptions.mutations.reactivateSubscription,
      {
        loadingMessage: "Reactivando suscripción...",
        successMessage: "Suscripción reactivada exitosamente",
        errorMessage: "Error al reactivar la suscripción",
      },
    );

  const { execute: setLifetime, bar: setLifetimeBar } = useToastMutation(
    api.companies.subscriptions.mutations.setSubscriptionLifetime,
    {
      loadingMessage: "Actualizando lifetime...",
      successMessage: "Suscripción lifetime actualizada",
      errorMessage: "Error al actualizar lifetime",
    },
  );

  if (!details) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded" />
                <div className="h-3 bg-muted rounded w-5/6" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const { company, subscription, plan, limitsStatus } = details;

  // Adapt data for SubscriptionStatusCard
  const subscriptionStatus =
    subscription && plan && limitsStatus
      ? {
          status: subscription.status,
          planName: plan.name,
          daysRemaining: subscription.trialEnd
            ? Math.max(
                0,
                Math.ceil(
                  (subscription.trialEnd - Date.now()) / (1000 * 60 * 60 * 24),
                ),
              )
            : 0,
          isTrial: subscription.status === "trialing",
          isGracePeriod:
            subscription.status === "past_due" || !!subscription.gracePeriodEnd,
          isActive: subscription.status === "active",
          limits: subscription.limits,
          currentUsage: {
            userCount: limitsStatus.users.current || 0,
            branchCount: limitsStatus.branches.current || 0,
            storageUsedGB: limitsStatus.storage.current || 0,
            invoicesThisMonth: limitsStatus.invoices.current || 0,
          },
          warnings: [
            ...(limitsStatus.users.isOverLimit
              ? [
                  {
                    type: "users_exceeded",
                    message: `Límite de usuarios excedido (${limitsStatus.users.current}/${limitsStatus.users.max})`,
                    severity: "error" as const,
                  },
                ]
              : limitsStatus.users.isNearLimit
                ? [
                    {
                      type: "users_near_limit",
                      message: `Límite de usuarios cerca del máximo (${limitsStatus.users.current}/${limitsStatus.users.max})`,
                      severity: "warning" as const,
                    },
                  ]
                : []),
            ...(limitsStatus.branches.isOverLimit
              ? [
                  {
                    type: "branches_exceeded",
                    message: `Límite de sucursales excedido (${limitsStatus.branches.current}/${limitsStatus.branches.max})`,
                    severity: "error" as const,
                  },
                ]
              : limitsStatus.branches.isNearLimit
                ? [
                    {
                      type: "branches_near_limit",
                      message: `Límite de sucursales cerca del máximo (${limitsStatus.branches.current}/${limitsStatus.branches.max})`,
                      severity: "warning" as const,
                    },
                  ]
                : []),
            ...(limitsStatus.invoices.isOverLimit
              ? [
                  {
                    type: "invoices_exceeded",
                    message: `Límite de facturas excedido (${limitsStatus.invoices.current}/${limitsStatus.invoices.max})`,
                    severity: "error" as const,
                  },
                ]
              : limitsStatus.invoices.isNearLimit
                ? [
                    {
                      type: "invoices_near_limit",
                      message: `Límite de facturas cerca del máximo (${limitsStatus.invoices.current}/${limitsStatus.invoices.max})`,
                      severity: "warning" as const,
                    },
                  ]
                : []),
            ...(subscription.status === "trialing" && subscription.trialEnd
              ? [
                  {
                    type: "trial_ending",
                    message: `Prueba termina en ${Math.ceil(
                      (subscription.trialEnd - Date.now()) /
                        (1000 * 60 * 60 * 24),
                    )} días`,
                    severity: "warning" as const,
                  },
                ]
              : []),
            ...(subscription.status === "past_due"
              ? [
                  {
                    type: "past_due",
                    message: "Suscripción vencida",
                    severity: "error" as const,
                  },
                ]
              : []),
            ...(subscription.status === "canceled"
              ? [
                  {
                    type: "canceled",
                    message: "Suscripción cancelada",
                    severity: "error" as const,
                  },
                ]
              : []),
          ],
          upgradeUrl: null,
          canUpgrade: true,
          enabledModules: subscription.enabledModules,
        }
      : null;

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    await cancelSubscription({
      companyId: company._id,
    });
  };

  const handleReactivateSubscription = async () => {
    if (!subscription) return;
    await reactivateSubscription({
      companyId: company._id,
    });
  };

  const handleToggleLifetime = async () => {
    if (!subscription) return;
    await setLifetime({
      companyId: company._id,
      lifetime: !subscription.lifetime,
    });
  };

  return (
    <div className="space-y-6">
      {subscription && plan ? (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Plan Actual: {plan.name}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {plan.description}
                  </CardDescription>
                </div>
                <Badge
                  variant={
                    subscription.status === "active" ? "default" : "secondary"
                  }
                  className="text-base px-4 py-1"
                >
                  {subscription.status === "active"
                    ? "Activa"
                    : subscription.status === "trialing"
                      ? "Prueba"
                      : subscription.status === "canceled"
                        ? "Cancelada"
                        : subscription.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Precio mensual
                  </p>
                  <p className="text-2xl font-bold">
                    {new Intl.NumberFormat("es-HN", {
                      style: "currency",
                      currency: "HNL",
                    }).format(plan.price)}
                  </p>
                </div>

                {subscription.trialEnd &&
                  subscription.status === "trialing" && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Próximo pago (fin de prueba)
                      </p>
                      <p className="text-lg font-semibold">
                        {format(subscription.trialEnd, "PP", {
                          locale: es,
                        })}
                      </p>
                    </div>
                  )}

                {subscription.status === "trialing" &&
                  subscription.trialEnd && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Fin de prueba
                      </p>
                      <p className="text-lg font-semibold">
                        {format(subscription.trialEnd, "PP", { locale: es })}
                      </p>
                    </div>
                  )}
              </div>

              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsChangePlanOpen(true)}
                >
                  Cambiar Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsEditLimitsOpen(true)}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Editar Límites
                </Button>
                <SubscriptionModulesManager
                  companyId={company._id}
                  currentModules={subscription.enabledModules || []}
                />
                {subscription.status !== "active" &&
                  subscription.status !== "canceled" && (
                    <Button
                      variant="default"
                      onClick={() => setIsManualActivateOpen(true)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Activar (pago manual)
                    </Button>
                  )}
                {(subscription.status === "trialing" ||
                  subscription.status === "past_due") && (
                  <Button
                    variant="outline"
                    onClick={() => setIsExtendTrialOpen(true)}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Extender prueba
                  </Button>
                )}
                <Button variant="outline" onClick={handleToggleLifetime}>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {subscription.lifetime ? "Quitar lifetime" : "Marcar lifetime"}
                </Button>
                {subscription.status === "active" ? (
                  <Button
                    variant="destructive"
                    onClick={handleCancelSubscription}
                  >
                    Cancelar Suscripción
                  </Button>
                ) : subscription.status === "canceled" ? (
                  <Button
                    variant="default"
                    onClick={handleReactivateSubscription}
                  >
                    Reactivar Suscripción
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Reuse SubscriptionStatusCard component */}
          <div className="max-w-2xl">
            {subscriptionStatus === undefined ? (
              <SubscriptionStatusCardSkeleton />
            ) : subscriptionStatus !== null ? (
              <SubscriptionStatusCard
                status={subscriptionStatus}
                onUpgrade={() => setIsChangePlanOpen(true)}
              />
            ) : null}
          </div>

          {/* Removed duplicate usage metrics section since SubscriptionStatusCard already shows them */}

          {/* Removed duplicate modules section since SubscriptionStatusCard already shows them */}
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Sin suscripción activa
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Esta empresa no tiene una suscripción configurada
            </p>
            <Button onClick={() => setIsChangePlanOpen(true)}>
              Asignar Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modales de gestión (super_admin) */}
      {subscription && (
        <SubscriptionLimitsModal
          isOpen={isEditLimitsOpen}
          subscription={subscription as SubscriptionData}
          onClose={() => setIsEditLimitsOpen(false)}
        />
      )}
      <ChangePlanModal
        isOpen={isChangePlanOpen}
        onClose={() => setIsChangePlanOpen(false)}
        companyId={company._id}
        currentPlanId={plan?._id}
        mode={subscription ? "change" : "assign"}
      />
      <ManualActivateModal
        isOpen={isManualActivateOpen}
        onClose={() => setIsManualActivateOpen(false)}
        companyId={company._id}
      />
      <ExtendTrialModal
        isOpen={isExtendTrialOpen}
        onClose={() => setIsExtendTrialOpen(false)}
        companyId={company._id}
      />
      {cancelBar}
      {reactivateBar}
      {setLifetimeBar}
    </div>
  );
}
